// @flow

import React, { Component } from 'react';
import { withRouter } from 'react-router';
import type { RouterHistory } from 'react-router';
import {
    Collapse,
    Navbar,
    NavbarToggler,
    NavbarBrand,
    Nav,
    NavItem,
    NavLink,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import AuthToken from '../../entities/AuthToken';
import type { ModeCollection } from '../modes';

const NAV_GROUPS = [
    {
        id: 'monitoring',
        label: 'Monitoring',
        titles: ['Operations', 'Restaurant Pulse', 'Support Hub', 'Launch Status', 'Contact Requests'],
    },
    {
        id: 'restaurants',
        label: 'Restaurants',
        titles: ['Organizations', 'Locations', 'Organization Users', 'Org User RTs', 'Reviews', 'Beacons', 'Menus'],
    },
    {
        id: 'guests-revenue',
        label: 'Guests & Revenue',
        titles: ['Users', 'Location Sessions', 'Bills', 'Transactions', 'Payment Methods', 'Reward Accounts', 'Reward Transactions'],
    },
    {
        id: 'admin-access',
        label: 'Admin & Access',
        titles: ['Admins', 'Admin RTs', 'Policies', 'Auth Sessions', 'Phone Checks'],
    },
];

type NavEntry = {
    name: string,
    routePath: string,
    navbarTitle: string,
};

type PropsType = {
    currentModeName: string,
    modes: ModeCollection,
    history: RouterHistory,
    isDarkMode: boolean,
    toggleTheme: () => void,
};

type StateType = {
    menuIsOpen: boolean,
    dropdownIsOpenById: {
        [string]: boolean,
    },
    mobileGroupOpenById: {
        [string]: boolean,
    },
};

class SiteNavbar extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            menuIsOpen: false,
            dropdownIsOpenById: {},
            mobileGroupOpenById: {},
        };
    }

    closeMenu() {
        this.setState({
            menuIsOpen: false,
            dropdownIsOpenById: {},
        });
    }

    navigateTo(path: string) {
        this.closeMenu();
        this.props.history.push(path);
    }

    buildDesktopDropdown(id: string, label: string, entries: Array<NavEntry>) {
        if (entries.length === 0) {
            return null;
        }

        const isActive = entries.some((entry) => entry.name === this.props.currentModeName);
        return (
            <Dropdown
                nav
                inNavbar
                key={`desktop-${id}`}
                className="d-none d-lg-block"
                isOpen={Boolean(this.state.dropdownIsOpenById[id])}
                toggle={() => this.toggleDropdown(id)}
                active={isActive}
            >
                <DropdownToggle nav caret>
                    {label}
                </DropdownToggle>
                <DropdownMenu right>
                    {entries.map((entry) => (
                        <DropdownItem
                            key={entry.name}
                            active={entry.name === this.props.currentModeName}
                            onClick={() => this.navigateTo(entry.routePath)}
                        >
                            {entry.navbarTitle}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </Dropdown>
        );
    }

    buildMobileGroup(id: string, label: string, entries: Array<NavEntry>) {
        if (entries.length === 0) {
            return null;
        }

        const isOpen = Boolean(this.state.mobileGroupOpenById[id]);
        const isActive = entries.some((entry) => entry.name === this.props.currentModeName);

        return (
            <NavItem key={`mobile-${id}`} className="d-lg-none site-navbar-mobile-group">
                <NavLink
                    href="#/"
                    className={`site-navbar-mobile-group-toggle${isActive ? ' is-active' : ''}`}
                    onClick={(event) => {
                        event.preventDefault();
                        this.toggleMobileGroup(id);
                    }}
                >
                    <span>{label}</span>
                    <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                </NavLink>
                {isOpen ? (
                    <div className="site-navbar-mobile-group-links">
                        {entries.map((entry) => (
                            <NavLink
                                key={entry.name}
                                href="#/"
                                active={entry.name === this.props.currentModeName}
                                className="site-navbar-mobile-link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    this.navigateTo(entry.routePath);
                                }}
                            >
                                {entry.navbarTitle}
                            </NavLink>
                        ))}
                    </div>
                ) : null}
            </NavItem>
        );
    }

    toggleNavbar() {
        this.setState((prevState) => ({
            menuIsOpen: !prevState.menuIsOpen,
            dropdownIsOpenById: {},
        }));
    }

    toggleDropdown(id: string) {
        this.setState((prevState) => ({
            dropdownIsOpenById: {
                ...prevState.dropdownIsOpenById,
                [id]: !prevState.dropdownIsOpenById[id],
            },
        }));
    }

    toggleMobileGroup(id: string) {
        this.setState((prevState) => ({
            mobileGroupOpenById: {
                ...prevState.mobileGroupOpenById,
                [id]: !prevState.mobileGroupOpenById[id],
            },
        }));
    }

    getGroupedEntries(): Array<{ id: string, label: string, entries: Array<NavEntry> }> {
        const allModeEntries = Object.entries(this.props.modes)
            .filter(([, mode]) => mode.priority !== undefined && mode.navbarTitle)
            .map(([name, mode]) => ({
                name,
                routePath: mode.routePath,
                navbarTitle: mode.navbarTitle,
            }));
        const entryByTitle = new Map(allModeEntries.map((entry) => [entry.navbarTitle, entry]));
        const usedNames = new Set();

        const groups = NAV_GROUPS
            .map((group) => {
                const entries = group.titles
                    .map((title) => entryByTitle.get(title))
                    .filter(Boolean);
                entries.forEach((entry) => usedNames.add(entry.name));
                return {
                    id: group.id,
                    label: group.label,
                    entries,
                };
            })
            .filter((group) => group.entries.length > 0);

        const fallbackEntries = allModeEntries.filter((entry) => !usedNames.has(entry.name));
        if (fallbackEntries.length) {
            groups.push({
                id: 'others',
                label: 'Others',
                entries: fallbackEntries,
            });
        }

        return groups;
    }

    render() {
        const groups = this.getGroupedEntries();

        return (
            <Navbar
                light={!this.props.isDarkMode}
                dark={this.props.isDarkMode}
                expand="lg"
                className="site-navbar"
            >
                <NavbarBrand
                    href="/"
                    className="site-navbar-brand"
                    onClick={(event) => {
                        event.preventDefault();
                        this.navigateTo('/');
                    }}
                >
                    <img
                        src={this.props.isDarkMode ? '/assets/logo_white.png' : '/assets/logo_black.png'}
                        alt="BillMaster Logo"
                        className="site-navbar-logo"
                    />
                    <div className="site-navbar-brand-copy">
                        <span className="site-navbar-brand-title">Backoffice</span>
                        <span className="site-navbar-brand-subtitle">Live ops · on the go</span>
                    </div>
                </NavbarBrand>
                <NavbarToggler
                    onClick={() => this.toggleNavbar()}
                    className="site-navbar-toggler"
                    aria-label="Toggle navigation menu"
                />
                <Collapse isOpen={this.state.menuIsOpen} navbar className="site-navbar-collapse">
                    <Nav className="ml-auto site-navbar-nav" navbar>
                        {groups.map((group) => this.buildDesktopDropdown(group.id, group.label, group.entries))}
                        {groups.map((group) => this.buildMobileGroup(group.id, group.label, group.entries))}
                        <NavItem>
                            <NavLink
                                href="#/"
                                className="site-navbar-utility-link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    this.props.toggleTheme();
                                }}
                            >
                                {this.props.isDarkMode ? 'Dark mode' : 'Light mode'}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#/"
                                className="site-navbar-utility-link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    AuthToken.unset();
                                    window.location.replace('/');
                                }}
                            >
                                Logout
                            </NavLink>
                        </NavItem>
                    </Nav>
                </Collapse>
            </Navbar>
        );
    }
}

export default withRouter(SiteNavbar);
